import type { CartLine } from '@/store/modules/cart';
import { cartCheckoutLog } from '@/utils/cartCheckoutDebug';
import { getStickerMinimumUnitCost } from '@/utils/priceWithFees';

export type CartAmountInputMode = 'donation' | 'ticketQuantity' | 'sweepstakesTier';

export interface CartAmountDialogItem {
  sticker: any;
  mode: CartAmountInputMode;
  minAmount: number;
  suggestedAmounts: number[];
  initialAmount: number;
  initialQuantity: number;
}

export interface CartAmountConfirmEntry {
  sticker: any;
  unitCost: number;
  quantity: number;
  donationAmount?: number;
  sweepstakesEntryCost?: number;
  donationCauseIds?: string[];
}

export interface CartCauseDialogItem {
  sticker: any;
  amount: number;
  quantity: number;
}

function normalizeCampaignType(sticker: any): string {
  return String(sticker?.campaignType || '').toLowerCase();
}

export function getCartAmountInputMode(sticker: any): CartAmountInputMode {
  const campaignType = normalizeCampaignType(sticker);
  if (campaignType === 'ticket') {
    return 'ticketQuantity';
  }
  if (campaignType === 'sweepstakes') {
    return 'sweepstakesTier';
  }
  return 'donation';
}

export function shouldShowShopQuantityStepper(sticker: any): boolean {
  return normalizeCampaignType(sticker) === 'ticket';
}

export function shouldPromptCartAmountBeforeAdd(sticker: any): boolean {
  const campaignType = normalizeCampaignType(sticker);
  const minCost = getStickerMinimumUnitCost(sticker);

  if (minCost <= 0 && campaignType !== 'ticket') {
    return false;
  }

  if (campaignType === 'ticket') {
    return true;
  }

  if (Array.isArray(sticker?.quickCosts) && sticker.quickCosts.length > 0) {
    return true;
  }

  if (campaignType === 'campaign' || campaignType === 'matchcampaign') {
    return true;
  }

  if (campaignType === 'sweepstakes') {
    return true;
  }

  // Fixed-price purchase stickers use the listed cost — no amount dialog needed.
  return false;
}

export function getSuggestedCartAmounts(sticker: any): number[] {
  if (Array.isArray(sticker?.quickCosts) && sticker.quickCosts.length > 0) {
    const costs = sticker.quickCosts
      .map((cost: number) => Number(cost))
      .filter((cost: number) => !Number.isNaN(cost) && cost > 0);
    return [...new Set(costs)].sort((a, b) => a - b);
  }

  if (
    normalizeCampaignType(sticker) === 'sweepstakes'
    && sticker?.sweepstakesSticker?.entryCost?.length
  ) {
    return sticker.sweepstakesSticker.entryCost
      .map((cost: number) => Number(cost))
      .filter((cost: number) => !Number.isNaN(cost) && cost > 0);
  }

  const min = getStickerMinimumUnitCost(sticker);
  if (min <= 0) {
    return [5, 10, 25, 50, 100];
  }

  const presets = [min, 25, 50, 100, 250]
    .filter((amount) => amount >= min);
  return [...new Set(presets.map((amount) => Math.round(amount * 100) / 100))].slice(0, 5);
}

export function buildCartAmountDialogItem(
  sticker: any,
  quantity = 1,
): CartAmountDialogItem {
  const mode = getCartAmountInputMode(sticker);
  const minAmount = getStickerMinimumUnitCost(sticker);
  const suggestedAmounts = getSuggestedCartAmounts(sticker);
  const initialAmount = mode === 'sweepstakesTier'
    ? (suggestedAmounts[0] ?? minAmount)
    : minAmount;

  return {
    sticker,
    mode,
    minAmount,
    suggestedAmounts,
    initialAmount,
    initialQuantity: Math.max(1, quantity),
  };
}

export function getStickerCauseOptions(sticker: any): string[] {
  let causes: string[] = [];
  if (Array.isArray(sticker?.causes)) {
    causes = sticker.causes;
  } else if (typeof sticker?.causes === 'string' && sticker.causes.trim()) {
    causes = sticker.causes.split(' |=| ');
  }
  return causes.filter((cause: string) => cause?.trim());
}

export function getCauseDisplayName(cause: string): string {
  const trimmed = cause?.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(' : ');
  return parts[0]?.trim() || trimmed;
}

export function getCauseIdFromOption(cause: string): string {
  const trimmed = cause?.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(' : ');
  return parts.length > 1 ? parts[1].trim() : trimmed;
}

export function parseStickerCauseIds(sticker: any): string[] {
  return getStickerCauseOptions(sticker)
    .map((cause: string) => getCauseIdFromOption(cause))
    .filter(Boolean);
}

export function stickerMayRequireCauseSelection(sticker: any): boolean {
  const campaignType = normalizeCampaignType(sticker);
  if (campaignType === 'sweepstakes' || campaignType === 'ticket') {
    return false;
  }

  return ['campaign', 'matchcampaign'].includes(campaignType)
    || (Array.isArray(sticker?.quickCosts) && sticker.quickCosts.length > 0);
}

export function stickerMissingCauseData(sticker: any): boolean {
  return stickerMayRequireCauseSelection(sticker)
    && getStickerCauseOptions(sticker).length === 0;
}

export function mergeStickerCauseFields(base: any, details: any): any {
  if (!details) {
    return base;
  }
  return {
    ...base,
    causes: details.causes ?? base.causes,
    isCausesSearchAllowed: details.isCausesSearchAllowed ?? base.isCausesSearchAllowed,
    sweepstakesSticker: details.sweepstakesSticker ?? base.sweepstakesSticker,
  };
}

export function stickerNeedsCauseSelection(sticker: any): boolean {
  if (!stickerMayRequireCauseSelection(sticker)) {
    return false;
  }

  return getStickerCauseOptions(sticker).length > 1;
}

function resolveCartLineCauseIds(
  sticker: any,
  explicitCauseIds?: string[],
): string[] {
  if (explicitCauseIds?.length) {
    return explicitCauseIds;
  }
  const causeIds = parseStickerCauseIds(sticker);
  return causeIds.length === 1 ? causeIds : [];
}

export function cartLineRequiresCause(
  line: Pick<
    CartLine,
    'lineType' | 'campaignType' | 'donationAmount' | 'donationCauseId'
    | 'donationCauseIds' | 'sweepstakesEntryCost'
  >,
): boolean {
  if (line.lineType === 'Donation') {
    return true;
  }
  if (line.lineType !== 'Sticker') {
    return false;
  }

  const campaignType = normalizeCampaignType({ campaignType: line.campaignType });
  if (campaignType === 'sweepstakes' || line.sweepstakesEntryCost != null) {
    return false;
  }
  if (campaignType === 'ticket') {
    return false;
  }

  return ['campaign', 'matchcampaign'].includes(campaignType)
    || line.donationAmount != null;
}

export function cartLineHasCause(
  line: Pick<CartLine, 'donationCauseId' | 'donationCauseIds'>,
): boolean {
  return !!(line.donationCauseId || line.donationCauseIds?.length);
}

export function getCartLinesMissingRequiredCause(lines: CartLine[]): CartLine[] {
  return lines.filter((line) => cartLineRequiresCause(line) && !cartLineHasCause(line));
}

export function buildShopCartLine(
  sticker: any,
  entry: Pick<
    CartAmountConfirmEntry,
    'unitCost' | 'quantity' | 'donationAmount' | 'sweepstakesEntryCost' | 'donationCauseIds'
  >,
): Omit<CartLine, 'id'> {
  const creatorTitle = sticker?.organization?.name
    || `${sticker?.user?.firstname || ''} ${sticker?.user?.lastname || ''}`.trim()
    || 'Spotlight';
  const campaignType = sticker?.campaignType;
  const causeIds = resolveCartLineCauseIds(sticker, entry.donationCauseIds);

  const line: Omit<CartLine, 'id'> = {
    stickerId: String(sticker.id),
    stickerTitle: sticker.title || '',
    creatorId: sticker?.organization?.name || sticker?.user?.id || String(sticker.id),
    creatorTitle,
    lineType: 'Sticker',
    unitCost: entry.unitCost,
    quantity: entry.quantity,
    campaignType,
    socialShopId: sticker?.socialShop?.id,
  };

  const donationAmount = entry.donationAmount ?? (entry.unitCost > 0 ? entry.unitCost : undefined);
  const isDonationCampaign = ['Campaign', 'MatchCampaign'].includes(campaignType)
    || (Array.isArray(sticker?.quickCosts) && sticker.quickCosts.length > 0);

  if (isDonationCampaign && donationAmount != null) {
    line.donationAmount = donationAmount;
    if (causeIds.length > 1) {
      line.donationCauseIds = causeIds;
    } else if (causeIds.length === 1) {
      line.donationCauseId = causeIds[0];
    }
  }

  if (entry.sweepstakesEntryCost != null) {
    line.sweepstakesEntryCost = entry.sweepstakesEntryCost;
    line.unitCost = entry.sweepstakesEntryCost;
  }

  cartCheckoutLog('buildShopCartLine', {
    stickerId: line.stickerId,
    stickerTitle: line.stickerTitle,
    campaignType: line.campaignType,
    explicitCauseIds: entry.donationCauseIds,
    resolvedCauseIds: causeIds,
    donationAmount: line.donationAmount,
    donationCauseId: line.donationCauseId,
    donationCauseIds: line.donationCauseIds,
    isDonationCampaign,
  });

  return line;
}

export function stickerHasInlineCartAmount(sticker: any, customAmount: number | undefined): boolean {
  if (!Array.isArray(sticker?.quickCosts) || !sticker.quickCosts.length) {
    return false;
  }
  return customAmount != null && !Number.isNaN(Number(customAmount)) && Number(customAmount) >= 0;
}
